// Billing / paywall (design/025 Phase 2).
//
// No access  → paywall: "Start your 7-day free trial" (or "Subscribe" when the
//              account already used its one trial) → Stripe Checkout.
// Web (Stripe) subscriber → status + "Manage subscription" (Stripe portal).
// App Store / Google Play subscriber → status + where to manage it.
// Staff (admin / publisher / event leader) → included with their role.
//
// Returning from Stripe (?checkout=success) we poll /me until the webhook has
// granted access — the redirect alone never grants it.
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import api from '../lib/api';
import { PageHead, Section } from '../components/ui';
import PlanPicker, { defaultPlan, planTerms, priceLine } from '../components/PlanPicker';

function fmtDate(iso) {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }); }
  catch { return iso; }
}

const errText = (e) => e?.data?.message || e?.message || 'Something went wrong';

export default function BillingPage() {
  const { me, refreshMe } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const checkout = params.get('checkout');
  // design/025 — first stop after "Create account".
  const welcome = params.get('welcome') === '1';
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(checkout === 'success');
  const pollRef = useRef(null);

  const sub = me?.subscription || {};
  const yearlyOn = !!sub.yearlyAvailable;
  const [plan, setPlan] = useState(defaultPlan(sub));
  useEffect(() => { setPlan(yearlyOn ? 'yearly' : 'monthly'); }, [yearlyOn]);

  // After Stripe redirects back: ask the gateway to confirm the checkout with
  // Stripe right away (doesn't depend on the webhook), then refresh.
  const sessionId = params.get('session_id');
  useEffect(() => {
    if (checkout !== 'success' || !sessionId) return;
    api.confirmCheckout(sessionId).then(() => refreshMe()).catch(() => { /* polling below still runs */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkout, sessionId]);

  // Backup: keep polling /me in case the confirm call failed.
  useEffect(() => {
    if (checkout !== 'success') return;
    if (sub.hasAccess) { setConfirming(false); return; }
    let tries = 0;
    pollRef.current = setInterval(async () => {
      tries += 1;
      try { await refreshMe(); } catch { /* keep trying */ }
      if (tries >= 15) { clearInterval(pollRef.current); setConfirming(false); }
    }, 2000);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkout, sub.hasAccess]);

  const dismiss = () => {
    const p = new URLSearchParams(params);
    p.delete('checkout'); p.delete('session_id');
    setParams(p, { replace: true });
  };

  const startCheckout = async () => {
    setBusy(true);
    try {
      const { url } = await api.startSubscriptionCheckout(plan);
      if (url) window.location.assign(url);
      else setBusy(false);
    } catch (e) {
      setBusy(false);
      if (e?.status === 409) { await refreshMe(); return; }   // already subscribed
      alert(errText(e));
    }
  };

  const openPortal = async () => {
    setBusy(true);
    // Don't spin forever if the server / Stripe never answers.
    const timeout = new Promise((_, reject) =>
      setTimeout(() => reject(new Error("The billing portal didn't open. Please try again in a moment.")), 20000));
    try {
      const { url } = await Promise.race([api.openBillingPortal(), timeout]);
      if (url) window.location.assign(url);
      else { setBusy(false); alert("The billing portal isn't available right now."); }
    } catch (e) {
      setBusy(false);
      alert(e?.status === 502
        ? "We couldn't reach Stripe to open the billing portal. Please try again in a moment."
        : errText(e));
    }
  };

  const Row = ({ k, v }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 0', borderBottom: '1px solid var(--border)', fontSize: '0.875rem' }}>
      <span style={{ color: 'var(--muted)' }}>{k}</span><b>{v}</b>
    </div>
  );

  // ---------------------------------------------------------------- banners
  const banners = (
    <>
      {checkout === 'success' && (sub.hasAccess ? (
        <div className="section" style={{ background: 'var(--green-bg, #dcfce7)', border: '1px solid #86efac', borderRadius: 10, padding: 14, display: 'flex', gap: 12, alignItems: 'center' }}>
          <div style={{ flex: 1, color: '#166534' }}>
            <strong>{sub.isInTrial ? 'Your free trial has started.' : "You're subscribed."}</strong> Welcome to Compassion Practice Premium.
          </div>
          <button className="btn" onClick={() => { dismiss(); navigate('/'); }}>Go to Home</button>
        </div>
      ) : confirming ? (
        <div className="section" style={{ background: 'var(--card-soft, #f9fafb)', border: '1px solid var(--border)', borderRadius: 10, padding: 14, color: 'var(--muted)' }}>
          Confirming your subscription… this can take a few seconds.
        </div>
      ) : (
        <div className="section" style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 10, padding: 14, color: '#92400e' }}>
          Payment received — it's taking a little longer than usual to activate. Refresh this page in a minute.
        </div>
      ))}
      {checkout === 'cancel' && (
        <div className="section" style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 10, padding: 14, display: 'flex', gap: 12, alignItems: 'center' }}>
          <div style={{ flex: 1, color: '#991b1b' }}>Checkout canceled — you haven't been charged.</div>
          <button className="btn" onClick={dismiss}>Dismiss</button>
        </div>
      )}
    </>
  );

  // ---------------------------------------------------------------- staff
  if (sub.adminBypass) {
    return (
      <div className="page">
        <PageHead title="Billing" sub="Subscription & plan" />
        <div style={{ maxWidth: 560 }}>
          <Section title="Your plan">
            <Row k="Access" v="Included with your role" />
            <div style={{ marginTop: 12, fontSize: '0.875rem', color: 'var(--muted)' }}>
              Publishers and event leaders have full access without a subscription.
            </div>
          </Section>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------- subscribed
  if (sub.hasAccess) {
    const provider = sub.provider;
    const renewOrEnd = sub.cancelAtPeriodEnd
      ? ['Access ends', fmtDate(sub.currentPeriodEnd) || '—']
      : [sub.isInTrial ? 'First charge' : 'Renews', fmtDate(sub.isInTrial ? (sub.trialEnd || sub.currentPeriodEnd) : sub.currentPeriodEnd) || '—'];
    return (
      <div className="page">
        <PageHead title="Billing" sub="Subscription & plan" />
        <div style={{ maxWidth: 560 }}>
          {banners}
          <Section title="Premium">
            <Row k="Status" v={sub.isInTrial ? 'Free trial' : (sub.status === 'GRACE' ? 'Payment issue — please update your card' : 'Active')} />
            {sub.plan && <Row k="Plan" v={sub.plan === 'YEARLY' ? 'Yearly' : 'Monthly'} />}
            {provider === 'STRIPE' && <Row k="Price" v={priceLine(sub.plan === 'YEARLY' ? 'yearly' : 'monthly')} />}
            {sub.isInTrial && sub.trialEnd && <Row k="Trial ends" v={fmtDate(sub.trialEnd)} />}
            <Row k={renewOrEnd[0]} v={renewOrEnd[1]} />
            {sub.cancelAtPeriodEnd && (
              <div style={{ marginTop: 10, fontSize: '0.85rem', color: '#92400e' }}>
                Your subscription is canceled and won't renew. You keep access until the date above.
              </div>
            )}
            <div style={{ marginTop: 16 }}>
              {provider === 'STRIPE' ? (
                <>
                  <button className="btn btn-primary" disabled={busy} onClick={openPortal}>
                    {busy ? 'Opening…' : 'Manage subscription'}
                  </button>
                  <div style={{ marginTop: 8, fontSize: '0.8rem', color: 'var(--muted)' }}>
                    Update your card, see invoices, or cancel — in the secure Stripe billing portal.
                  </div>
                </>
              ) : provider === 'APPLE' ? (
                <div style={{ fontSize: '0.875rem', color: 'var(--muted)' }}>
                  You subscribed in the App Store. Manage or cancel it on your iPhone: <b>Settings → your name → Subscriptions</b>.
                </div>
              ) : provider === 'GOOGLE' ? (
                <div style={{ fontSize: '0.875rem', color: 'var(--muted)' }}>
                  You subscribed on Google Play. Manage or cancel it in the <b>Play Store → Profile → Payments & subscriptions → Subscriptions</b>.
                </div>
              ) : null}
            </div>
          </Section>
          <Legal />
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------- paywall
  const trial = !!sub.trialEligible;
  // Before launch (server paywall off) new accounts may skip this step.
  const canSkip = !sub.paywallOn;
  return (
    <div className="page">
      <PageHead
        title={welcome ? 'Welcome! One last step' : 'Compassion Practice Premium'}
        sub={welcome
          ? 'Your account is ready. Start your free trial to get full access — on the web, iPhone and Android.'
          : 'Everything in one place — on the web, iPhone and Android'} />
      <div style={{ maxWidth: 560 }}>
        {banners}
        <Section>
          <div style={{ fontSize: '1.6rem', fontWeight: 800 }}>
            {trial ? '7 days free' : priceLine(plan)}
          </div>
          <div style={{ color: 'var(--muted)', marginBottom: 14 }}>
            {trial ? `then ${priceLine(plan)}. Cancel anytime.` : 'Cancel anytime.'}
          </div>
          {yearlyOn && <PlanPicker value={plan} onChange={setPlan} disabled={busy} />}
          <ul style={{ lineHeight: 1.9, paddingLeft: 20, marginTop: 0 }}>
            <li>All free courses and practices</li>
            <li>Join events and groups, and their conversations</li>
            <li>Your notes and reflections, synced across devices</li>
            <li>One subscription works on the web, iPhone and Android</li>
          </ul>
          <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', padding: '12px 16px', fontSize: '1rem' }}
            disabled={busy} onClick={startCheckout}>
            {busy ? 'Redirecting to secure checkout…' : (trial ? 'Start my free trial' : `Subscribe — ${priceLine(plan)}`)}
          </button>
          <div style={{ marginTop: 10, fontSize: '0.78rem', color: 'var(--muted)', lineHeight: 1.5 }}>
            {planTerms(plan, trial)}
            {' '}Paid courses, practices, events and groups are priced separately. Payments are processed securely by Stripe.
          </div>
          {canSkip && (
            <div style={{ textAlign: 'center', marginTop: 12 }}>
              <a onClick={() => navigate('/', { replace: true })} style={{ cursor: 'pointer', fontSize: '0.85rem' }}>
                Not now
              </a>
            </div>
          )}
        </Section>
        <Legal />
      </div>
    </div>
  );
}

function Legal() {
  return (
    <div style={{ fontSize: '0.8rem', color: 'var(--muted)', marginTop: 8 }}>
      <a href="/terms" target="_blank" rel="noreferrer">Terms of Use</a>
      {' · '}
      <a href="/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>
    </div>
  );
}
